import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * WE2 P2-05: SettingsModal (Lean Shell Architecture)
 * Modular 2-column TradingView-parity Chart Settings Modal
 */
import React, { useEffect, useRef, useState, useMemo, useCallback, useContext } from "react";
import { createPortal } from "react-dom";
import { useWidgetI18n } from "../../context/WidgetI18nContext";
import { usePortalAdapter } from "../../hooks/usePortalAdapter";
import { useDialogFocus } from "../../hooks/useDialogFocus";
import { SaaSFeatureContext } from "../../contexts/SaaSFeatureContext";
import { SettingsProvider, useSettingsContext } from "./SettingsContext";
import { useWorkspaceSettings } from "./WorkspaceSettingsContext";
import { useOptionalWorkspacePaperVisibility } from "../../context/WorkspacePaperVisibilityContext";
import { APPLIED_APPEARANCE_EVENT, APPLY_APPEARANCE_EVENT, extractAppearancePayload, } from "./model/appearancePayload";
import { settingsTabKeywords } from "./model/settingsTabKeywords";
import { useSettingsRollback } from "./model/useSettingsRollback";
import styles from "./SettingsModal.module.css";
import { SymbolSettingsTab } from "./tabs/SymbolSettingsTab";
import { StatusLineSettingsTab } from "./tabs/StatusLineSettingsTab";
import { ScalesSettingsTab } from "./tabs/ScalesSettingsTab";
import { AppearanceSettingsTab } from "./tabs/AppearanceSettingsTab";
import { PanesLayoutSettingsTab } from "./tabs/PanesLayoutSettingsTab";
import { IndicatorsSettingsTab } from "./tabs/IndicatorsSettingsTab";
import { TradingEventsSettingsTab } from "./tabs/TradingEventsSettingsTab";
import { ThemePaletteSettingsTab } from "./tabs/ThemePaletteSettingsTab";
import { LicensingSettingsTab } from "./tabs/LicensingSettingsTab";
import { ResetSettingsTab } from "./tabs/ResetSettingsTab";
const TABS = [
    { id: "symbol", labelKey: "settings.tab.symbol", defaultLabel: "Symbol & Candles", icon: "📊" },
    { id: "statusLine", labelKey: "settings.tab.statusLine", defaultLabel: "Status Line", icon: "📈" },
    { id: "scales", labelKey: "settings.tab.scales", defaultLabel: "Scales & Coordinates", icon: "📐" },
    { id: "appearance", labelKey: "settings.tab.appearance", defaultLabel: "Appearance & Canvas", icon: "🎨" },
    { id: "panes", labelKey: "settings.tab.panes", defaultLabel: "Panes Layout", icon: "🔲" },
    { id: "indicators", labelKey: "settings.tab.indicators", defaultLabel: "Indicators", icon: "⚡" },
    { id: "trading", labelKey: "settings.tab.trading", defaultLabel: "Trading & Events", icon: "💼" },
    { id: "theme", labelKey: "settings.tab.theme", defaultLabel: "Theme & Colors", icon: "🌓" },
    { id: "licensing", labelKey: "settings.tab.licensing", defaultLabel: "Licensing", icon: "🔑" },
    { id: "reset", labelKey: "settings.tab.reset", defaultLabel: "Reset Defaults", icon: "🔄" },
];
function SettingsModalShell(props) {
    const { onClose, children, selectedPaneId, onSelectedPaneIdChange, comparisonOverlays, onComparisonRemove, onComparisonUpdate, onExportLayout, onImportLayout, toggleTheme, onThemePresetChange, isDark, onReset, chartId, symbol, timeframe, seriesTier, } = props;
    const { activeTab, setActiveTab, searchQuery, setSearchQuery, resetAll, settings, updateSection } = useSettingsContext();
    // P1-1 (C1a): Cancel phải HOÀN TÁC thật (settings áp live) — xem useSettingsRollback.
    const handleCancel = useSettingsRollback(settings, updateSection, () => onClose?.());
    const { t } = useWidgetI18n();
    const saasContext = useContext(SaaSFeatureContext);
    const { isMultiChart } = useWorkspaceSettings();
    const paperVisibility = useOptionalWorkspacePaperVisibility();
    const [applyFeedback, setApplyFeedback] = useState(null);
    const safeT = useCallback((key, fallback) => {
        const val = t(key);
        return !val || val === key ? fallback : val;
    }, [t]);
    // Apply-to-All feedback: provider fans out, then fires the done event.
    useEffect(() => {
        if (typeof window === "undefined")
            return;
        const handler = (e) => {
            const detail = e.detail;
            const mine = chartId ?? null;
            if (!detail || detail.sourceChartId == null || detail.sourceChartId !== mine)
                return;
            setApplyFeedback(safeT("settings.applyToAllDone", `Applied appearance to ${detail.applied ?? 0} chart(s)`).replace("{count}", String(detail.applied ?? 0)));
        };
        window.addEventListener(APPLIED_APPEARANCE_EVENT, handler);
        return () => window.removeEventListener(APPLIED_APPEARANCE_EVENT, handler);
    }, [chartId, safeT]);
    useEffect(() => {
        if (!applyFeedback)
            return;
        const timer = setTimeout(() => setApplyFeedback(null), 2600);
        return () => clearTimeout(timer);
    }, [applyFeedback]);
    const handleApplyToAll = useCallback(() => {
        if (!chartId)
            return;
        try {
            const payload = extractAppearancePayload(settings);
            // Chart-centric visibility rides along: source chart's paper state
            // fans out to every chart (user's "apply to all" expectation).
            const paperEnabled = paperVisibility != null ? (paperVisibility.perChart[chartId] ?? paperVisibility.master) : undefined;
            if (typeof window !== "undefined") {
                window.dispatchEvent(new CustomEvent(APPLY_APPEARANCE_EVENT, { detail: { sourceChartId: chartId, payload, paperEnabled } }));
            }
        }
        catch {
            // Best-effort: source chart already persists its own edits live.
        }
    }, [chartId, settings, paperVisibility]);
    const headerTitle = useMemo(() => {
        if (isMultiChart && (symbol || timeframe)) {
            const ctx = [symbol, timeframe].filter(Boolean).join(" · ");
            return safeT("settings.headerTitleActive", `Chart Settings — ${ctx}`).replace("{symbol}", symbol ?? "").replace("{timeframe}", timeframe ?? "");
        }
        return safeT("settings.dialogKicker", "Chart Settings");
    }, [isMultiChart, symbol, timeframe, safeT]);
    const visibleTabs = useMemo(() => {
        let list = TABS;
        if (!saasContext) {
            list = list.filter((tab) => tab.id !== "licensing");
        }
        if (!searchQuery.trim())
            return list;
        const q = searchQuery.toLowerCase();
        return list.filter((tab) => {
            const label = safeT(tab.labelKey, tab.defaultLabel).toLowerCase();
            if (label.includes(q) || tab.id.toLowerCase().includes(q) || tab.defaultLabel.toLowerCase().includes(q))
                return true;
            // P1-1: lọc cả theo TỪ KHOÁ NỘI DUNG (C1b) — gõ "volume"/"crosshair" phải ra tab.
            return settingsTabKeywords(tab.id).some((kw) => kw.includes(q));
        });
    }, [saasContext, searchQuery, safeT]);
    // Keyboard arrow navigation
    const handleKeyDown = (e) => {
        if (e.key === "Escape") {
            onClose?.();
            return;
        }
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const currentIndex = visibleTabs.findIndex((t) => t.id === activeTab);
            if (currentIndex === -1)
                return;
            const nextIndex = e.key === "ArrowDown"
                ? (currentIndex + 1) % visibleTabs.length
                : (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
            setActiveTab(visibleTabs[nextIndex].id);
        }
    };
    const renderActiveTabContent = () => {
        if (children)
            return children;
        switch (activeTab) {
            case "symbol":
                return _jsx(SymbolSettingsTab, {});
            case "statusLine":
                return _jsx(StatusLineSettingsTab, {});
            case "scales":
                return _jsx(ScalesSettingsTab, {});
            case "appearance":
                return _jsx(AppearanceSettingsTab, {});
            case "panes":
                return (_jsx(PanesLayoutSettingsTab, { selectedPaneId: selectedPaneId, onSelectedPaneIdChange: onSelectedPaneIdChange, onExportLayout: onExportLayout, onImportLayout: onImportLayout }));
            case "indicators":
            case "indicatorSets":
                return (_jsx(IndicatorsSettingsTab, { chartId: chartId, selectedPaneId: selectedPaneId, onSelectedPaneIdChange: onSelectedPaneIdChange, comparisonOverlays: comparisonOverlays, onComparisonRemove: onComparisonRemove, onComparisonUpdate: onComparisonUpdate, seriesTier: seriesTier }));
            case "trading":
                return _jsx(TradingEventsSettingsTab, { chartId: chartId, symbol: symbol });
            case "theme":
                return (_jsx(ThemePaletteSettingsTab, { toggleTheme: toggleTheme, isDark: isDark, onThemePresetChange: onThemePresetChange }));
            case "licensing":
                return _jsx(LicensingSettingsTab, {});
            case "reset":
                return _jsx(ResetSettingsTab, { onReset: onReset });
            default:
                return _jsx(SymbolSettingsTab, {});
        }
    };
    return (_jsxs("div", { className: styles.modalShell, "data-testid": "settings-modal", onKeyDown: handleKeyDown, tabIndex: -1, children: [_jsxs("div", { className: styles.header, children: [_jsxs("h2", { className: styles.headerTitle, children: [headerTitle, isMultiChart && (symbol || timeframe) && (_jsx("span", { className: styles.activeBadge, children: safeT("settings.activeBadge", "Active chart") }))] }), _jsx("button", { type: "button", className: styles.closeBtn, onClick: onClose, title: safeT("settings.dialogClose", "Close"), "data-testid": "settings-close-btn", children: "\u00D7" })] }), _jsxs("div", { className: styles.body, children: [_jsxs("div", { className: styles.sidebar, children: [_jsx("div", { className: styles.searchWrapper, children: _jsx("input", { type: "text", className: styles.searchInput, placeholder: safeT("settings.searchPlaceholder", "Search settings..."), value: searchQuery, onChange: (e) => setSearchQuery(e.target.value) }) }), _jsx("ul", { className: styles.tabList, role: "tablist", children: visibleTabs.map((tab) => {
                                    const isActive = activeTab === tab.id;
                                    return (_jsx("li", { role: "presentation", children: _jsxs("button", { type: "button", role: "tab", "aria-selected": isActive, className: `${styles.tabItem} ${isActive ? styles.tabItemActive : ""}`, onClick: () => setActiveTab(tab.id), children: [_jsx("span", { className: styles.tabIcon, children: tab.icon }), _jsx("span", { children: safeT(tab.labelKey, tab.defaultLabel) })] }) }, tab.id));
                                }) })] }), _jsx("div", { className: styles.contentPanel, role: "tabpanel", children: renderActiveTabContent() })] }), _jsxs("div", { className: styles.footer, children: [_jsxs("div", { className: styles.footerLeft, children: [_jsx("button", { type: "button", className: styles.btnReset, "data-testid": "settings-reset-defaults-btn", onClick: () => {
                                    resetAll();
                                    onReset?.();
                                }, children: safeT("settings.restoreDefaults", "Restore Defaults") }), isMultiChart && chartId && (_jsx("button", { type: "button", className: styles.btnApplyAll, "data-testid": "settings-apply-to-all-btn", title: safeT("settings.applyToAllNote", "Copy appearance & style to all charts (symbol, panes and scale modes are kept)"), onClick: handleApplyToAll, children: applyFeedback || safeT("settings.applyToAll", "Apply to All") }))] }), _jsxs("div", { className: styles.footerRight, children: [_jsx("button", { type: "button", className: styles.btnCancel, onClick: handleCancel, children: safeT("settings.cancel", "Cancel") }), _jsx("button", { type: "button", className: styles.btnApply, onClick: onClose, children: safeT("settings.apply", "Apply") })] })] })] }));
}
export function SettingsModal(props) {
    const { open, onClose, isDark = true, themePreset, portalAdapter, chartId, storageAdapter, onOverlayTimeframeChange, onOverlayStyleChange, onMaxVisiblePanesChange, onShowNonTradingDaysChange, onTimezoneChange, onAlertAudioEnabledChange, onEnableWhaleChange, onWhaleThresholdChange, onEnableCVDChange, onCVDModeChange, onDataAdapterChange, toggleTheme, onReset, overlayTimeframe, overlayStyle, maxVisiblePanes, showNonTradingDays, showTimeScrollbar, onShowTimeScrollbarChange, timezone, alertAudioEnabled, enableWhale, whaleThreshold, enableCVD, cvdMode, dataAdapterName, } = props;
    const { portalTarget } = usePortalAdapter({ portalAdapter: portalAdapter });
    const backdropRef = useRef(null);
    // UX-02: shared dialog focus contract — initial focus, Tab loop, Escape and
    // focus restore. Replaces the ad-hoc window-level Escape listener, which
    // also fired when focus was still outside the modal.
    useDialogFocus(backdropRef, { open, onEscape: () => onClose?.() });
    if (!open)
        return null;
    const activeChartMode = isDark ? "dark" : "light";
    const activeChartTheme = themePreset || (isDark ? "classic-dark" : "modern-light");
    return createPortal(_jsx("div", { ref: backdropRef, className: `vnsc-root vnsc-settings-backdrop ${styles.backdrop}`, "data-chart-mode": activeChartMode, "data-chart-theme": activeChartTheme, "data-chart-id": chartId, "data-testid": "settings-backdrop", onClick: (e) => {
            if (e.target === backdropRef.current)
                onClose?.();
        }, children: _jsx(SettingsProvider, { isDark: isDark, themePreset: themePreset, storageAdapter: storageAdapter, chartId: chartId, onOverlayTimeframeChange: onOverlayTimeframeChange, onOverlayStyleChange: onOverlayStyleChange, onMaxVisiblePanesChange: onMaxVisiblePanesChange, onShowNonTradingDaysChange: onShowNonTradingDaysChange, onTimezoneChange: onTimezoneChange, onAlertAudioEnabledChange: onAlertAudioEnabledChange, onEnableWhaleChange: onEnableWhaleChange, onWhaleThresholdChange: onWhaleThresholdChange, onEnableCVDChange: onEnableCVDChange, onCVDModeChange: onCVDModeChange, onDataAdapterChange: onDataAdapterChange, toggleTheme: toggleTheme, onReset: onReset, overlayTimeframe: overlayTimeframe, overlayStyle: overlayStyle, maxVisiblePanes: maxVisiblePanes, showNonTradingDays: showNonTradingDays, showTimeScrollbar: showTimeScrollbar, onShowTimeScrollbarChange: onShowTimeScrollbarChange, timezone: timezone, alertAudioEnabled: alertAudioEnabled, enableWhale: enableWhale, whaleThreshold: whaleThreshold, enableCVD: enableCVD, cvdMode: cvdMode, dataAdapterName: dataAdapterName, children: _jsx(SettingsModalShell, { ...props }) }) }), portalTarget || (typeof document !== "undefined" ? document.body : null));
}
